import { NavLink } from 'react-router-dom';
import {
  Building2,
  Tag,
  Receipt,
  Inbox,
  X,
  Settings,
  Crown,
  Database,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AdminNavItem {
  nameKey: string;
  to: string;
  icon: typeof Building2;
  end?: boolean;
}

const cockpitItems: AdminNavItem[] = [
  { nameKey: 'navigation:menu.adminTenants', to: '/admin/tenants', icon: Building2 },
  { nameKey: 'navigation:menu.adminPlans', to: '/admin/subscription-plans', icon: Tag },
  { nameKey: 'navigation:menu.adminTenantInvoices', to: '/admin/tenant-invoices', icon: Receipt },
  { nameKey: 'navigation:menu.adminSupport', to: '/admin/support', icon: Inbox },
  { nameKey: 'navigation:menu.adminDemoData', to: '/admin/demo-data', icon: Database },
];

const accountItems: AdminNavItem[] = [
  { nameKey: 'navigation:menu.adminAccount', to: '/admin/profile', icon: Settings },
];

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const { t } = useTranslation(['navigation', 'common']);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed lg:sticky top-0 inset-y-0 left-0 z-30 w-[212px] bg-white border-r border-slate-200 h-screen transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Brand block */}
          <div className="flex items-center gap-2 px-4 h-14 border-b border-slate-200">
            <NavLink
              to="/admin/tenants"
              onClick={onClose}
              className="flex items-center gap-2 flex-1"
            >
              <div className="w-8 h-8 bg-[#00A871] rounded flex items-center justify-center text-white font-bold text-sm">
                FF
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-semibold text-[15px] text-slate-900 tracking-tight">
                  FacilityFlow
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono flex items-center gap-1">
                  <Crown className="w-2.5 h-2.5" strokeWidth={2} />
                  Admin
                </span>
              </div>
            </NavLink>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded hover:bg-slate-50 text-slate-500"
              aria-label={t('common:actions.close')}
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-2">
            <div>
              <div className="kicker px-4 pt-5 pb-2">
                {t('navigation:groups.adminCockpit')}
              </div>
              {cockpitItems.map((item) => (
                <AdminSidebarLink key={item.nameKey} item={item} onClose={onClose} t={t} />
              ))}
            </div>
            <div>
              <div className="kicker px-4 pt-5 pb-2">
                {t('navigation:groups.adminAccount')}
              </div>
              {accountItems.map((item) => (
                <AdminSidebarLink key={item.nameKey} item={item} onClose={onClose} t={t} />
              ))}
            </div>
          </nav>

          {/* Footer */}
          <div className="kicker mt-auto px-4 py-3 border-t border-slate-200">
            {`Version ${__APP_VERSION__}`}
          </div>
        </div>
      </aside>
    </>
  );
}

interface AdminSidebarLinkProps {
  item: AdminNavItem;
  onClose: () => void;
  t: (key: string) => string;
}

function AdminSidebarLink({ item, onClose, t }: AdminSidebarLinkProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => onClose()}
      className={({ isActive }) =>
        `flex items-center gap-2.5 mx-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
          isActive
            ? 'bg-[#00A871] text-white font-medium'
            : 'text-slate-600 hover:bg-slate-50'
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
      <span>{t(item.nameKey)}</span>
    </NavLink>
  );
}
